/**
 * StaffClearanceHub Component
 *
 * Purpose:
 * - Allow directors to configure external clearance URLs for each required clearance type.
 * - Staff can click "Visit" to open the external resource in a new tab.
 * - Follows the same pattern as PayrollSubtab.jsx (save URL → open in new tab).
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ExternalLink,
  Settings,
  RefreshCw,
  ShieldCheck,
  Fingerprint,
  Search,
  Link as LinkIcon,
  Plus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLEARANCES = [
  {
    key: "childAbuseUrl",
    title: "Child Abuse History Clearance",
    description: "Child Abuse History Clearance — required for all staff working with children.",
    icon: ShieldCheck,
    iconBg: "bg-blue-500/30",
    iconColor: "text-blue-200",
    placeholder: "https://...",
  },
  {
    key: "statePoliceUrl",
    title: "State Police Criminal History",
    description: "State Police Request for Criminal History — background screening for all employees.",
    icon: Search,
    iconBg: "bg-indigo-500/30",
    iconColor: "text-indigo-200",
    placeholder: "https://...",
  },
  {
    key: "fbiUrl",
    title: "FBI Fingerprint Clearance",
    description: "Federal Bureau of Investigation fingerprint-based criminal history check.",
    icon: Fingerprint,
    iconBg: "bg-cyan-500/30",
    iconColor: "text-cyan-200",
    placeholder: "https://...",
  },
  {
    key: "nsorUrl",
    title: "National Sex Offender Registry (NSOR)",
    description: "DOWNLOAD FORM & MAIL (cannot be done online) — required verification for all childcare staff.",
    icon: ShieldCheck,
    iconBg: "bg-sky-500/30",
    iconColor: "text-sky-200",
    placeholder: "https://...",
  },
];

const DEFAULT_SETTINGS = {
  childAbuseUrl: "",
  statePoliceUrl: "",
  fbiUrl: "",
  nsorUrl: "",
  custom1Label: "",
  custom1Url: "",
  custom2Label: "",
  custom2Url: "",
};

export const StaffClearanceHub = ({ isOwner }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff-clearance-hub/settings`);
      if (res.data) setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/staff-clearance-hub/settings`, settings);
      toast.success("Clearance hub settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const isValidUrl = (str) => {
    try { new URL(str); return true; } catch { return false; }
  };

  const openUrl = (url) => {
    if (url && isValidUrl(url)) window.open(url, "_blank", "noopener,noreferrer");
  };

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading clearance hub...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div
        className="relative rounded-2xl overflow-hidden shadow-2xl"
        data-testid="clearance-hub-hero"
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("https://customer-assets.emergentagent.com/job_6e815e9a-eae1-4a7d-a2b1-981bafc8af69/artifacts/95ykg0mh_image.png")',
          }}
        />
        <div className="absolute inset-0 bg-blue-900/40" />

        <div className="relative z-10 p-8 md:p-12">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-900/70 px-4 py-2 rounded-full mb-6 border border-white/30">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white text-sm font-bold tracking-wide">Staff Clearance Resources</span>
            </div>

            {/* Title row */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-4 bg-blue-900/70 rounded-2xl shadow-lg border border-white/20">
                <ShieldCheck className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
                  Staff Clearance Hub
                </h1>
                <p className="text-white text-lg font-bold">
                  Central access to all required background clearance portals
                </p>
              </div>
            </div>

            <p className="text-white text-base mb-8 max-w-2xl font-semibold">
              {isOwner
                ? "Configure the URLs below for each clearance type. Staff will see direct links to visit these portals from this page."
                : "Click any button below to visit the official clearance portal and complete your required background checks."}
            </p>

            {/* ── Standard Clearance Cards ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CLEARANCES.map(({ key, title, description, icon: Icon, iconBg, iconColor, placeholder }) => (
                <div
                  key={key}
                  className="bg-blue-900/75 rounded-2xl p-5 border border-white/20 space-y-3 shadow-lg"
                  data-testid={`clearance-card-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${iconBg} rounded-lg flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">{title}</p>
                      {key === "nsorUrl" ? (
                        <p className="text-xs font-medium">
                          <span className="text-yellow-300 font-extrabold tracking-wide">DOWNLOAD FORM & MAIL (cannot be done online)</span>
                          <span className="text-white/90"> — required verification for all childcare staff.</span>
                        </p>
                      ) : (
                        <p className="text-white/90 text-xs font-medium">{description}</p>
                      )}
                    </div>
                  </div>

                  {isOwner ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="url"
                        value={settings[key]}
                        onChange={(e) => set(key, e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 h-10 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl"
                        data-testid={`input-${key}`}
                      />
                      <Button
                        onClick={() => openUrl(settings[key])}
                        disabled={!settings[key] || !isValidUrl(settings[key])}
                        size="sm"
                        className="h-10 px-4 bg-white text-blue-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                        data-testid={`open-${key}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Visit
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => openUrl(settings[key])}
                      disabled={!settings[key] || !isValidUrl(settings[key])}
                      className="w-full h-10 bg-white text-blue-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                      data-testid={`open-${key}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {settings[key] && isValidUrl(settings[key]) ? "Visit Portal" : "URL Not Configured"}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* ── Additional Needed Requirements Slots ── */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { labelKey: "custom1Label", urlKey: "custom1Url", num: 1 },
                { labelKey: "custom2Label", urlKey: "custom2Url", num: 2 },
              ].map(({ labelKey, urlKey, num }) => (
                <div
                  key={urlKey}
                  className="bg-blue-900/75 rounded-2xl p-5 border border-white/20 border-dashed space-y-3 shadow-lg"
                  data-testid={`clearance-card-custom${num}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/15 rounded-lg">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-white font-bold text-sm drop-shadow">
                      Additional Needed Requirement {num}
                    </p>
                  </div>

                  {isOwner ? (
                    <div className="space-y-2">
                      <Input
                        type="text"
                        value={settings[labelKey]}
                        onChange={(e) => set(labelKey, e.target.value)}
                        placeholder="Requirement Name"
                        className="h-9 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl font-medium"
                        data-testid={`input-${labelKey}`}
                      />
                      <div className="flex gap-2">
                        <Input
                          type="url"
                          value={settings[urlKey]}
                          onChange={(e) => set(urlKey, e.target.value)}
                          placeholder="https://..."
                          className="flex-1 h-10 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl"
                          data-testid={`input-${urlKey}`}
                        />
                        <Button
                          onClick={() => openUrl(settings[urlKey])}
                          disabled={!settings[urlKey] || !isValidUrl(settings[urlKey])}
                          size="sm"
                          className="h-10 px-4 bg-white text-blue-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                          data-testid={`open-${urlKey}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Visit
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {settings[labelKey] && (
                        <p className="text-white/90 font-medium text-sm">{settings[labelKey]}</p>
                      )}
                      <Button
                        onClick={() => openUrl(settings[urlKey])}
                        disabled={!settings[urlKey] || !isValidUrl(settings[urlKey])}
                        className="w-full h-10 bg-white text-blue-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                        data-testid={`open-${urlKey}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {settings[urlKey] && isValidUrl(settings[urlKey])
                          ? settings[labelKey] || "Visit Portal"
                          : "URL Not Configured"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Save Button (owner only) ── */}
      {isOwner && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 text-base font-semibold shadow-lg rounded-xl"
            data-testid="clearance-hub-save-btn"
          >
            <Settings className="w-5 h-5 mr-2" />
            {saving ? "Saving..." : "Save Clearance Hub Settings"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StaffClearanceHub;
